'use client';

import type { ReactNode } from 'react';
import { MetaDefault } from '../../../../../components/typography';
import { useTranslations } from 'next-intl';
import type { CardRenderContext } from '../../tool-config-types';
import { FlatToolCard } from '../shared/flat-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import { HitlLabeledValues } from '../shared/hitl-labeled-values';
import { toRecord } from '../shared/network-tool-card-utils';

type SignalGenerationToolArgs = {
  targetDomains?: string[];
};

function readDomainList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const domains = value.filter(
    (domain): domain is string =>
      typeof domain === 'string' && domain.trim().length > 0,
  );

  return domains.length > 0 ? domains : undefined;
}

function parseSignalGenerationArgs(
  input: unknown,
): SignalGenerationToolArgs | null {
  const record = toRecord(input);

  if (!record) {
    return null;
  }

  const softwareId = record.softwareId;
  const scanId = record.scanId;

  if (typeof softwareId !== 'string' || softwareId.trim().length === 0) {
    return null;
  }

  if (typeof scanId !== 'string' || scanId.trim().length === 0) {
    return null;
  }

  const targetDomains =
    readDomainList(record.targetDomains) ?? readDomainList(record.seedDomains);

  return targetDomains ? { targetDomains } : {};
}

function SignalGenerationCard({
  title,
  args,
  error,
  isRunning,
  headerActions,
}: {
  title: string;
  args: SignalGenerationToolArgs;
  error?: string;
  isRunning: boolean;
  headerActions?: ReactNode;
}) {
  const t = useTranslations(
    'designSystem.chat.externalNetworkSignalGenerationTool',
  );

  return (
    <FlatToolCard
      title={title}
      isRunning={isRunning}
      headerActions={headerActions}
    >
      {args.targetDomains ? (
        <HitlLabeledValues
          label={t('targetDomainsLabel')}
          values={args.targetDomains}
        />
      ) : null}
      {error ? <MetaDefault color="fg.error">{error}</MetaDefault> : null}
    </FlatToolCard>
  );
}

export function renderExternalNetworkSignalGenerationCard(
  ctx: CardRenderContext,
): ReactNode {
  const { toolCallData, toolName } = ctx;
  const isStarted =
    toolCallData.status === 'started' || toolCallData.status === 'in-progress';

  if (
    !isStarted &&
    toolCallData.status !== 'completed' &&
    toolCallData.status !== 'error'
  ) {
    return undefined;
  }

  const args = parseSignalGenerationArgs(toolCallData.input);

  if (!args) {
    return undefined;
  }

  return (
    <SignalGenerationCard
      title={toolName}
      args={args}
      error={toolCallData.status === 'error' ? toolCallData.error : undefined}
      isRunning={toolCallData.status === 'in-progress'}
      headerActions={getHITLArtifactHeaderActions(ctx)}
    />
  );
}
